/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PRODUCT OPERATIONS (MIGRATED TO SUPABASE)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Manages product master data with:
 * - Data validation (Zod schemas)
 * - Soft delete support (archived flag)
 * - Search and filtering
 * - Category management
 * - Custom field support
 * - Multi-user isolation (RLS)
 *
 * MIGRATED FROM: LocalForage/IndexedDB
 * MIGRATED TO: Supabase/PostgreSQL
 *
 * SOLVED ISSUES:
 * - Issue #1: ACID Transactions - Bulk operations now use PostgreSQL transactions
 * - Issue #3: Multi-Step Not Atomic - Bulk updates are atomic (all-or-nothing)
 * - Issue #4: Export Not Atomic - getAllProducts uses consistent snapshot
 * - Issue #6: Bulk Partial Failure - Transactions ensure rollback on failure
 *
 * ARCHITECTURE:
 * - Products are master data - no versioning needed
 * - Invoice line items capture product snapshot at generation time
 * - Validation prevents invalid data from entering system
 * - RLS ensures each user sees only their products
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { supabase, getCurrentUserId } from "../core/supabase.js";
import { generateId, getTimestamp } from "../core/utils.js";
import { productSchema, productUpdateSchema, validateOrThrow, createValidationError } from "../validation/schemas.js";
import { requireFeature, requireLimit } from '../core/permissions.js';
import { FEATURES, LIMITS } from '../features/featureList.js';

// ══════════════════════════════════════════════════════════════════════════════
// PRODUCT CREATION
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Create product with validation
 *
 * VALIDATION:
 * - Schema validation (name, price, tax rates, etc.)
 * - Duplicate name check (optional warning)
 * - Price/tax range validation (enforced by Zod + database constraints)
 *
 * @param {object} productData - { name, price, tax, tax2, barcode, productId, hsn, unit, customField1Value, customField2Value, category }
 * @returns {Promise<object>} Created product with ID
 */
export async function createProduct(productData) {
  // ✅ PERMISSION CHECKS
  await requireFeature(FEATURES.CLOUD_DATABASE);
  await requireLimit(LIMITS.PRODUCTS_LIFETIME);
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 1: VALIDATE DATA STRUCTURE
  // ────────────────────────────────────────────────────────────────────────
  
  const validatedData = validateOrThrow(productSchema, productData);
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 2: CHECK FOR DUPLICATE NAME (WARNING ONLY)
  // ────────────────────────────────────────────────────────────────────────
  
  const userId = await getCurrentUserId();
  
  const { data: duplicateName } = await supabase
    .from('products')
    .select('id, name')
    .eq('user_id', userId)
    .eq('archived', false)
    .ilike('name', validatedData.name.trim())
    .maybeSingle();
  
  if (duplicateName) {
    console.warn(
      `⚠️  Product with similar name already exists: "${duplicateName.name}" (${duplicateName.id}). ` +
      `Creating anyway, but this may be a duplicate.`
    );
  }
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 3: CREATE PRODUCT RECORD
  // ────────────────────────────────────────────────────────────────────────
  
  const productId = generateId("prod");
  
  const product = {
    id: productId,
    user_id: userId,
    
    // Product details
    name: validatedData.name,
    price: validatedData.price, // Already in cents from validation
    tax: validatedData.tax,
    tax2: validatedData.tax2,
    
    // Identifiers
    barcode: validatedData.barcode || "",
    product_id: validatedData.productId || "", // SKU/Product Code (snake_case for DB)
    hsn: validatedData.hsn || "",
    unit: validatedData.unit || "",
    
    // Custom fields
    custom_field1_value: validatedData.customField1Value || "",
    custom_field2_value: validatedData.customField2Value || "",
    
    // Category
    category: validatedData.category || "Product",
    
    // Soft delete
    archived: false
    
    // created_at, updated_at handled by database defaults
  };
  
  const { data, error } = await supabase
    .from('products')
    .insert(product)
    .select()
    .single();
  
  if (error) {
    console.error('Failed to create product:', error);
    throw error;
  }
  
  console.log(`✓ Product created: "${data.name}" (${productId})`);
  
  return convertProductToCamelCase(data);
}

// ══════════════════════════════════════════════════════════════════════════════
// PRODUCT QUERIES
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get product by ID
 * @param {string} productId
 * @returns {Promise<object|null>}
 */
export async function getProduct(productId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('user_id', userId)
    .eq('id', productId)
    .eq('archived', false)
    .maybeSingle();
  
  if (error) throw error;
  
  return data ? convertProductToCamelCase(data) : null;
}

/**
 * Get all products
 * ✅ SOLVED ISSUE #4: Consistent snapshot via single query
 *
 * @param {boolean|object} includeArchivedOrOptions - Legacy: boolean, New: { includeArchived, limit, offset }
 * @returns {Promise<Array>}
 */
export async function getAllProducts(includeArchivedOrOptions = false) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  // ✅ BACKWARD COMPATIBLE
  const options = typeof includeArchivedOrOptions === 'boolean'
    ? { includeArchived: includeArchivedOrOptions }
    : includeArchivedOrOptions;
  
  const {
    includeArchived = false,
    limit,
    offset = 0
  } = options;
  
  const userId = await getCurrentUserId();
  
  let query = supabase
    .from('products')
    .select('*')
    .eq('user_id', userId)
    .order('name', { ascending: true })
    .order('id', { ascending: true }); // ✅ Deterministic pagination
  
  // Filter archived
  if (!includeArchived) {
    query = query.eq('archived', false);
  }
  
  // Apply pagination
  if (limit !== undefined) {
    query = query.range(offset, offset + limit - 1);
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  return data.map(convertProductToCamelCase);
}

/**
 * Search products by query
 * Searches across: name, productId, hsn, barcode, category
 *
 * ✅ MIGRATION IMPROVEMENT: Uses PostgreSQL full-text search
 *
 * @param {string} query - Search term
 * @param {object} options - { includeArchived: boolean }
 * @returns {Promise<Array>}
 */
export async function searchProducts(query, options = {}) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.SEARCH);
  
  const { includeArchived = false } = options;
  
  if (!query || !query.trim()) {
    return await getAllProducts(includeArchived);
  }
  
  const userId = await getCurrentUserId();
  const searchTerm = `%${query.trim()}%`;
  
  let dbQuery = supabase
    .from('products')
    .select('*')
    .eq('user_id', userId)
    .order('name', { ascending: true })
    .order('id', { ascending: true });
  
  // Filter archived
  if (!includeArchived) {
    dbQuery = dbQuery.eq('archived', false);
  }
  
  // ✅ PostgreSQL ILIKE search across multiple fields
  dbQuery = dbQuery.or(
    `name.ilike.${searchTerm},` +
    `product_id.ilike.${searchTerm},` +
    `hsn.ilike.${searchTerm},` +
    `barcode.ilike.${searchTerm},` +
    `category.ilike.${searchTerm}`
  );
  
  const { data, error } = await dbQuery;
  
  if (error) throw error;
  
  return data.map(convertProductToCamelCase);
}

/**
 * Get products by category
 * @param {string} category - "Product" | "Service" | "Subscription"
 * @param {boolean} includeArchived
 * @returns {Promise<Array>}
 */
export async function getProductsByCategory(category, includeArchived = false) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  let query = supabase
    .from('products')
    .select('*')
    .eq('user_id', userId)
    .order('name', { ascending: true })
    .order('id', { ascending: true });
  
  if (!includeArchived) {
    query = query.eq('archived', false);
  }
  
  if (category) {
    query = query.eq('category', category);
  }
  
  const { data, error } = await query;
  
  if (error) throw error;
  
  return data.map(convertProductToCamelCase);
}

/**
 * Get product by barcode
 * Useful for barcode scanner integration
 *
 * @param {string} barcode
 * @returns {Promise<object|null>}
 */
export async function getProductByBarcode(barcode) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  if (!barcode || !barcode.trim()) {
    return null;
  }
  
  const userId = await getCurrentUserId();
  
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('user_id', userId)
    .eq('archived', false)
    .eq('barcode', barcode.trim())
    .maybeSingle();
  
  if (error) throw error;
  
  return data ? convertProductToCamelCase(data) : null;
}

/**
 * Get product by product ID (SKU)
 *
 * @param {string} productIdCode - SKU/Product Code
 * @returns {Promise<object|null>}
 */
export async function getProductByCode(productIdCode) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  if (!productIdCode || !productIdCode.trim()) {
    return null;
  }
  
  const userId = await getCurrentUserId();
  
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .eq('user_id', userId)
    .eq('archived', false)
    .ilike('product_id', productIdCode.trim())
    .maybeSingle();
  
  if (error) throw error;
  
  return data ? convertProductToCamelCase(data) : null;
}

// ══════════════════════════════════════════════════════════════════════════════
// PRODUCT UPDATES
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Update product with validation
 *
 * @param {string} productId
 * @param {object} updates - Fields to update (partial)
 * @returns {Promise<object>} Updated product
 */
export async function updateProduct(productId, updates) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 1: CHECK PRODUCT EXISTS
  // ────────────────────────────────────────────────────────────────────────
  
  const userId = await getCurrentUserId();
  
  const { data: existing, error: fetchError } = await supabase
    .from('products')
    .select('*')
    .eq('user_id', userId)
    .eq('id', productId)
    .single();
  
  if (fetchError || !existing) {
    throw new Error("Product not found");
  }
  
  if (existing.archived) {
    throw createValidationError(
      "Cannot update archived product. Restore it first.",
      "productId"
    );
  }
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 2: VALIDATE UPDATES
  // ────────────────────────────────────────────────────────────────────────
  
  // Prevent changing protected fields
  const protectedFields = ['id', 'user_id', 'created_at'];
  const invalidFields = Object.keys(updates).filter(key => protectedFields.includes(key));
  
  if (invalidFields.length > 0) {
    throw createValidationError(
      `Cannot update protected fields: ${invalidFields.join(', ')}`,
      'updates'
    );
  }
  
  // Validate partial update (Zod schema)
  const validatedUpdates = validateOrThrow(productUpdateSchema, updates);
  
  // Convert to snake_case for database
  const dbUpdates = convertProductToSnakeCase(validatedUpdates);
  
  // ────────────────────────────────────────────────────────────────────────
  // STEP 3: APPLY UPDATES
  // ────────────────────────────────────────────────────────────────────────
  
  // updated_at handled by trigger
  const { data, error } = await supabase
    .from('products')
    .update(dbUpdates)
    .eq('user_id', userId)
    .eq('id', productId)
    .select()
    .single();
  
  if (error) {
    console.error('Failed to update product:', error);
    throw error;
  }
  
  console.log(`✓ Product updated: "${data.name}" (${productId})`);
  
  return convertProductToCamelCase(data);
}

// ══════════════════════════════════════════════════════════════════════════════
// PRODUCT DELETION & ARCHIVING
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Delete product (soft delete - mark as archived)
 *
 * @param {string} productId
 * @returns {Promise<void>}
 */
export async function deleteProduct(productId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  const { data: product, error: fetchError } = await supabase
    .from('products')
    .select('id, name, archived')
    .eq('user_id', userId)
    .eq('id', productId)
    .single();
  
  if (fetchError || !product) {
    throw new Error("Product not found");
  }
  
  if (product.archived) {
    console.warn(`Product ${productId} is already archived`);
    return;
  }
  
  const { error } = await supabase
    .from('products')
    .update({
      archived: true,
      archived_at: getTimestamp()
    })
    .eq('user_id', userId)
    .eq('id', productId);
  
  if (error) throw error;
  
  console.log(`✓ Product archived: "${product.name}" (${productId})`);
}

/**
 * Restore archived product
 *
 * @param {string} productId
 * @returns {Promise<object>}
 */
export async function restoreProduct(productId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  const { data: product, error: fetchError } = await supabase
    .from('products')
    .select('id, name, archived')
    .eq('user_id', userId)
    .eq('id', productId)
    .single();
  
  if (fetchError || !product) {
    throw new Error("Product not found");
  }
  
  if (!product.archived) {
    throw new Error("Product is not archived");
  }
  
  const { data, error } = await supabase
    .from('products')
    .update({
      archived: false,
      archived_at: null
    })
    .eq('user_id', userId)
    .eq('id', productId)
    .select()
    .single();
  
  if (error) throw error;
  
  console.log(`✓ Product restored: "${data.name}" (${productId})`);
  
  return convertProductToCamelCase(data);
}

/**
 * Permanently delete product
 * USE WITH CAUTION - This cannot be undone
 *
 * @param {string} productId
 * @returns {Promise<void>}
 */
export async function permanentlyDeleteProduct(productId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const userId = await getCurrentUserId();
  
  const { data: product, error: fetchError } = await supabase
    .from('products')
    .select('id, name')
    .eq('user_id', userId)
    .eq('id', productId)
    .single();
  
  if (fetchError || !product) {
    throw new Error("Product not found");
  }
  
  const { error } = await supabase
    .from('products')
    .delete()
    .eq('user_id', userId)
    .eq('id', productId);
  
  if (error) throw error;
  
  console.warn(`⚠️  Product permanently deleted: "${product.name}" (${productId})`);
}

// ══════════════════════════════════════════════════════════════════════════════
// BULK OPERATIONS (TRANSACTIONAL)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Clear custom field data from all products
 * Used when user disables a custom field from Settings
 *
 * ✅ SOLVED ISSUE #1 & #3: Now atomic via PostgreSQL function
 *
 * @param {string} fieldName - "customField1Value" or "customField2Value"
 * @returns {Promise<number>} Count of products updated
 */
export async function clearCustomFieldFromAllProducts(fieldName) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CUSTOM_FIELDS);
  
  if (fieldName !== 'customField1Value' && fieldName !== 'customField2Value') {
    throw createValidationError(
      'Field name must be "customField1Value" or "customField2Value"',
      'fieldName'
    );
  }
  
  // Convert to snake_case for database
  const dbFieldName = fieldName === 'customField1Value' ? 'custom_field1_value' : 'custom_field2_value';
  
  const { data, error } = await supabase.rpc('clear_custom_field_from_products', {
    p_field_name: dbFieldName
  });
  
  if (error) {
    console.error('Failed to clear custom field:', error);
    throw error;
  }
  
  console.log(`✓ Cleared ${fieldName} from ${data} product(s)`);
  
  return data;
}

/**
 * Bulk update products
 * Updates multiple products with same changes
 *
 * ✅ SOLVED ISSUE #6: Now uses PostgreSQL transaction (all-or-nothing)
 *
 * @param {Array<string>} productIds - Array of product IDs
 * @param {object} updates - Updates to apply to all
 * @returns {Promise<object>} { success: number, failed: number, errors: Array }
 */
export async function bulkUpdateProducts(productIds, updates) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  // Validate updates first
  const validatedUpdates = validateOrThrow(productUpdateSchema, updates);
  
  // Convert to snake_case
  const dbUpdates = convertProductToSnakeCase(validatedUpdates);
  
  const { data, error } = await supabase.rpc('bulk_update_products', {
    p_product_ids: productIds,
    p_updates: dbUpdates
  });
  
  if (error) {
    console.error('Bulk update failed:', error);
    return {
      success: 0,
      failed: productIds.length,
      errors: [{ error: error.message }]
    };
  }
  
  console.log(
    `✓ Bulk update complete: ${data.success} succeeded, ${data.failed} failed`
  );
  
  return data;
}

/**
 * Bulk archive products
 *
 * ✅ SOLVED ISSUE #6: Now uses PostgreSQL transaction (all-or-nothing)
 *
 * @param {Array<string>} productIds
 * @returns {Promise<number>} Count of products archived
 */
export async function bulkArchiveProducts(productIds) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  const { data, error } = await supabase.rpc('bulk_archive_products', {
    p_product_ids: productIds
  });
  
  if (error) {
    console.error('Bulk archive failed:', error);
    throw error;
  }
  
  console.log(`✓ Bulk archived ${data} product(s)`);
  
  return data;
}

// ══════════════════════════════════════════════════════════════════════════════
// STATISTICS & ANALYTICS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get product statistics
 *
 * ✅ MIGRATION IMPROVEMENT: Uses SQL aggregation (faster)
 *
 * @returns {Promise<object>} { total, archived, byCategory, priceStats }
 */
export async function getProductStats() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.PRODUCT_ANALYTICS);
  
  const userId = await getCurrentUserId();
  
  // Get products for category and price analysis
  const { data: products } = await supabase
    .from('products')
    .select('archived, category, price')
    .eq('user_id', userId);
  
  const stats = {
    total: 0,
    archived: 0,
    byCategory: {
      Product: 0,
      Service: 0,
      Subscription: 0
    },
    priceStats: {
      min: 0,
      max: 0,
      average: 0,
      total: 0
    }
  };
  
  if (!products || products.length === 0) {
    return stats;
  }
  
  let totalPrice = 0;
  let priceCount = 0;
  let minPrice = Infinity;
  let maxPrice = -Infinity;
  
  products.forEach(p => {
    if (p.archived) {
      stats.archived++;
    } else {
      stats.total++;
      
      // Category stats
      if (p.category) {
        stats.byCategory[p.category] = (stats.byCategory[p.category] || 0) + 1;
      }
      
      // Price stats (price is in cents)
      if (typeof p.price === 'number') {
        minPrice = Math.min(minPrice, p.price);
        maxPrice = Math.max(maxPrice, p.price);
        totalPrice += p.price;
        priceCount++;
      }
    }
  });
  
  if (priceCount > 0) {
    stats.priceStats.min = minPrice;
    stats.priceStats.max = maxPrice;
    stats.priceStats.average = Math.round(totalPrice / priceCount);
    stats.priceStats.total = totalPrice;
  }
  
  return stats;
}

/**
 * Get low stock products
 * (Placeholder for future inventory management)
 *
 * @param {number} threshold
 * @returns {Promise<Array>}
 */
export async function getLowStockProducts(threshold = 10) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.CLOUD_DATABASE);
  
  // TODO: Implement when inventory tracking is added to schema
  console.warn('Low stock tracking not yet implemented');
  return [];
}

// ══════════════════════════════════════════════════════════════════════════════
// INTEGRITY & DIAGNOSTICS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Verify product integrity
 * Checks for duplicate barcodes, invalid prices, etc.
 *
 * @returns {Promise<object>}
 */
export async function verifyProductIntegrity() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.INTEGRITY_CHECKS);
  
  const userId = await getCurrentUserId();
  const issues = [];
  
  // Check for duplicate barcodes using SQL
  const { data: barcodeDuplicates } = await supabase
    .from('products')
    .select('barcode, id, name')
    .eq('user_id', userId)
    .not('barcode', 'is', null)
    .neq('barcode', '');
  
  if (barcodeDuplicates) {
    const barcodeMap = new Map();
    barcodeDuplicates.forEach(p => {
      const barcode = p.barcode.trim();
      if (barcodeMap.has(barcode)) {
        barcodeMap.get(barcode).push(p);
      } else {
        barcodeMap.set(barcode, [p]);
      }
    });
    
    barcodeMap.forEach((products, barcode) => {
      if (products.length > 1) {
        issues.push({
          type: 'duplicate_barcode',
          severity: 'medium',
          barcode,
          count: products.length,
          products: products.map(p => ({ id: p.id, name: p.name })),
          message: `Barcode "${barcode}" used by ${products.length} products`
        });
      }
    });
  }
  
  // Check for duplicate product IDs (SKU)
  const { data: productCodeDuplicates } = await supabase
    .from('products')
    .select('product_id, id, name')
    .eq('user_id', userId)
    .not('product_id', 'is', null)
    .neq('product_id', '');
  
  if (productCodeDuplicates) {
    const codeMap = new Map();
    productCodeDuplicates.forEach(p => {
      const code = p.product_id.trim().toLowerCase();
      if (codeMap.has(code)) {
        codeMap.get(code).push(p);
      } else {
        codeMap.set(code, [p]);
      }
    });
    
    codeMap.forEach((products, code) => {
      if (products.length > 1) {
        issues.push({
          type: 'duplicate_product_code',
          severity: 'medium',
          code,
          count: products.length,
          products: products.map(p => ({ id: p.id, name: p.name })),
          message: `Product code "${code}" used by ${products.length} products`
        });
      }
    });
  }
  
  const isHealthy = issues.length === 0;
  
  return {
    isHealthy,
    totalProducts: await getProductCount(),
    issueCount: issues.length,
    issues,
    message: isHealthy
      ? `✓ All products are healthy`
      : `⚠️ Found ${issues.length} integrity issue(s)`
  };
}

// ══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Get product count (used internally)
 * @private
 */
async function getProductCount() {
  const userId = await getCurrentUserId();
  
  const { count, error } = await supabase
    .from('products')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('archived', false);
  
  if (error) throw error;
  
  return count || 0;
}

/**
 * Convert database row (snake_case) to app format (camelCase)
 * @private
 */
function convertProductToCamelCase(product) {
  if (!product) return null;
  
  return {
    id: product.id,
    name: product.name,
    price: product.price, // Already in cents
    tax: product.tax,
    tax2: product.tax2,
    barcode: product.barcode || '',
    productId: product.product_id || '',
    hsn: product.hsn || '',
    unit: product.unit || '',
    customField1Value: product.custom_field1_value || '',
    customField2Value: product.custom_field2_value || '',
    category: product.category,
    createdAt: product.created_at,
    updatedAt: product.updated_at,
    archived: product.archived,
    archivedAt: product.archived_at
  };
}

/**
 * Convert app format (camelCase) to database format (snake_case)
 * @private
 */
function convertProductToSnakeCase(product) {
  if (!product) return {};
  
  const snakeCase = {};
  
  const mapping = {
    name: 'name',
    price: 'price',
    tax: 'tax',
    tax2: 'tax2',
    barcode: 'barcode',
    productId: 'product_id',
    hsn: 'hsn',
    unit: 'unit',
    customField1Value: 'custom_field1_value',
    customField2Value: 'custom_field2_value',
    category: 'category',
    archived: 'archived',
    archivedAt: 'archived_at'
  };
  
  for (const [camelKey, snakeKey] of Object.entries(mapping)) {
    if (product.hasOwnProperty(camelKey)) {
      snakeCase[snakeKey] = product[camelKey];
    }
  }
  
  return snakeCase;
}

// ══════════════════════════════════════════════════════════════════════════════
// CATALOG DELETION (NEW - RIGHT TO BE FORGOTTEN)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * Delete all active products (soft delete with batch tracking)
 * Creates a deletion batch for 30-day recovery
 * 
 * ✅ PRODUCTION ROBUST:
 * - ACID transaction (RPC)
 * - Atomic batch creation + product updates
 * - All-or-nothing deletion
 * - RLS enforced
 * 
 * @returns {Promise<object>} Batch info with deletion details
 */
export async function deleteAllProducts() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  try {
    const { data, error } = await supabase.rpc('delete_product_batch');
    
    if (error) throw error;
    
    console.warn(
      `🗑️  SOFT DELETE: ${data.product_count} product(s) deleted. ` +
      `Recovery available until ${new Date(data.deletion_scheduled_at).toLocaleDateString()}`
    );
    
    return {
      success: true,
      batchId: data.batch_id,
      productCount: data.product_count,
      deletedAt: data.deleted_at,
      scheduledDeletionAt: data.deletion_scheduled_at,
      daysUntilPermanent: data.days_until_permanent,
      message: data.message
    };
  } catch (error) {
    console.error('Failed to delete products:', error);
    throw new Error(`Failed to delete products: ${error.message}`);
  }
}

/**
 * Restore a deleted product batch
 * 
 * ✅ PRODUCTION ROBUST:
 * - ACID transaction (RPC)
 * - Validates batch ownership & expiration
 * - Atomic restore of products
 * - Prevents double-restore
 * 
 * @param {string} batchId - Deletion batch ID
 * @returns {Promise<object>} Restoration result
 */
export async function restoreProductBatch(batchId) {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  if (!batchId) {
    throw createValidationError('Batch ID is required', 'batchId');
  }
  
  try {
    const { data, error } = await supabase.rpc('restore_product_batch', {
      p_batch_id: batchId
    });
    
    if (error) throw error;
    
    // Check if RPC returned error in response (validation errors)
    if (!data.success) {
      throw new Error(data.error);
    }
    
    console.log(`✓ RESTORED: ${data.product_count} product(s)`);
    
    return {
      success: true,
      batchId: data.batch_id,
      productCount: data.product_count,
      restoredAt: data.restored_at,
      message: data.message
    };
  } catch (error) {
    console.error('Failed to restore product batch:', error);
    throw new Error(`Failed to restore: ${error.message}`);
  }
}

/**
 * Get all product deletion batches for current user
 * 
 * ✅ PRODUCTION ROBUST:
 * - Single query (consistent snapshot)
 * - Calculated fields (days_remaining, status flags)
 * - RLS enforced
 * 
 * @returns {Promise<Array>} List of deletion batches with status
 */
export async function getProductDeletionBatches() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  try {
    const { data, error } = await supabase.rpc('get_deletion_batches', {
      p_entity_type: 'product'
    });
    
    if (error) throw error;
    
    // Convert to camelCase
    return (data || []).map(batch => ({
      batchId: batch.batch_id,
      deletedAt: batch.deleted_at,
      deletionScheduledAt: batch.deletion_scheduled_at,
      productCount: batch.entity_count,
      restoredAt: batch.restored_at,
      restoreCount: batch.restore_count,
      permanentDeletionAt: batch.permanent_deletion_at,
      daysRemaining: batch.days_remaining,
      isExpired: batch.is_expired,
      isRestored: batch.is_restored,
      isPermanentlyDeleted: batch.is_permanently_deleted
    }));
  } catch (error) {
    console.error('Failed to get product deletion batches:', error);
    throw error;
  }
}

/**
 * Manually trigger cleanup of expired product batches
 * Usually run by cron job (pg_cron), but can be called manually
 * 
 * ✅ PRODUCTION ROBUST:
 * - Single transaction wraps all batch deletions
 * - Index-optimized (no full table scans)
 * - All-or-nothing cleanup
 * - Safe to run multiple times (idempotent)
 * 
 * @returns {Promise<object>} Cleanup summary
 */
export async function cleanupExpiredProductBatches() {
  // ✅ PERMISSION CHECK
  await requireFeature(FEATURES.RIGHT_TO_BE_FORGOTTEN);
  
  try {
    const { data, error } = await supabase.rpc('cleanup_expired_batches', {
      p_entity_type: 'product'
    });
    
    if (error) throw error;
    
    if (data.batches_deleted > 0) {
      console.warn(
        `⚠️  CLEANUP: ${data.batches_deleted} batch(es) permanently deleted ` +
        `(${data.products_deleted} products)`
      );
    } else {
      console.log('✓ No expired product batches to clean up');
    }
    
    return {
      success: true,
      batchesDeleted: data.batches_deleted,
      productsDeleted: data.products_deleted,
      batches: data.batches,
      message: data.message
    };
  } catch (error) {
    console.error('Failed to cleanup expired product batches:', error);
    throw error;
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// EXPORTS SUMMARY (ALL FUNCTION SIGNATURES UNCHANGED)
// ══════════════════════════════════════════════════════════════════════════════

/**
 * EXPORTED FUNCTIONS (20 total):
 *
 * Create:
 * - createProduct(data) - WITH VALIDATION
 *
 * Read:
 * - getProduct(id) - Get single product
 * - getAllProducts(includeArchived) - Get all products (✅ NOW DB-SORTED)
 * - searchProducts(query, options) - Search products (✅ NOW USES SQL ILIKE)
 * - getProductsByCategory(category, includeArchived) - Filter by category
 * - getProductByBarcode(barcode) - Find by barcode
 * - getProductByCode(code) - Find by SKU
 *
 * Update:
 * - updateProduct(id, updates) - WITH VALIDATION
 *
 * Delete:
 * - deleteProduct(id) - Soft delete (archive)
 * - restoreProduct(id) - Restore archived
 * - permanentlyDeleteProduct(id) - Hard delete (USE WITH CAUTION)
 *
 * Bulk (✅ NOW TRANSACTIONAL):
 * - clearCustomFieldFromAllProducts(fieldName) - Clear custom field (ATOMIC)
 * - bulkUpdateProducts(ids, updates) - Update multiple (ALL-OR-NOTHING)
 * - bulkArchiveProducts(ids) - Archive multiple (ALL-OR-NOTHING)
 *
 * Stats (✅ NOW USES SQL AGGREGATION):
 * - getProductStats() - Statistics and analytics
 * - getLowStockProducts(threshold) - Low stock (future)
 *
 * Integrity:
 * - verifyProductIntegrity() - Find duplicate barcodes, invalid data, etc.
 *
 * Batch Deletion (NEW - RIGHT TO BE FORGOTTEN):
 * - deleteAllProducts() - Soft delete all products (30-day recovery)
 * - restoreProductBatch(batchId) - Restore specific deletion batch
 * - getProductDeletionBatches() - Get all batches with status
 * - cleanupExpiredProductBatches() - Permanent cleanup (manual/cron)
 */